import { Button } from '@/app/common/components/button/Button'
import DefaultAvatar from '@/app/pages/profile/components/DefaultAvatar'
import { cn, currencyFormatter } from '@/lib/utils'
import { Badge } from '@/shad/ui/badge'
import { AlertTriangle, FileWarningIcon, X } from 'lucide-react'
import { StudentAwardInformation } from '../../award-form'

interface ManualAwardFundedStudentWarningProps {
  selectedStudents: StudentAwardInformation[]
  onProceed: () => void
  onCancel: () => void
}

export default function ManualAwardFundedStudentWarning({
  selectedStudents,
  onProceed,
  onCancel,
}: ManualAwardFundedStudentWarningProps) {
  const fundedStudents = selectedStudents.filter(
    (student) => student.status === 'Funded'
  )
  const fundedCount = fundedStudents.length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center size-12 flex-shrink-0 bg-danger/20  rounded-lg">
          <AlertTriangle className="size-8 text-danger" />
        </div>
        <div>
          <h2 className="text-lg font-medium  text-danger/100">
            Already Awarded Students
          </h2>
          <p className="text-sm text-gray-500">
            {fundedCount === 1
              ? '1 of the selected students has already received an award. Are you sure you want to give them an additional award?'
              : `${fundedCount} of the selected students have already received awards. Are you sure you want to give them additional awards?`}{' '}
          </p>{' '}
        </div>
      </div>

      {/* Funded Students List */}
      <div className="">
        <div className="">
          {fundedStudents.map((student) => (
            <div
              key={student.id}
              className={cn('p-4 border-b border-gray-100 last:border-b-0')}
            >
              <div className="flex items-center gap-3">
                <div className="w-full">
                  <div className="flex gap-2 items-center min-w-0">
                    <DefaultAvatar
                      variant="neutral"
                      firstName={student.first_name}
                      lastName={student.last_name}
                    />
                    <div className="w-full">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900 text-sm truncate">
                            {student.first_name} {student.last_name}
                          </h4>
                          <span className="text-xs text-gray-500">
                            ID: {student.id}
                          </span>
                        </div>
                        <Badge className="ml-4" variant={'destructive'}>
                          Already Awarded
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {student.applciant_email}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    {student.total_awarded_amount && (
                      <div className="text-sm flex items-center justify-between text-gray-800">
                        <div className="text-sm text-gray-800 ">
                          Previously awarded
                        </div>
                        <div className="text-base font-medium text-gray-900">
                          {currencyFormatter.format(
                            student.total_awarded_amount
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4">
        <Button
          variant="outline"
          onClick={onCancel}
          className="flex items-center gap-2"
        >
          <X className="h-4 w-4" />
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={onProceed}
          className="flex items-center gap-2"
        >
          <FileWarningIcon className="h-4 w-4" />
          Proceed with Additional Awards
        </Button>
      </div>
    </div>
  )
}
